import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Toolbar,
  Tooltip,
  Stack,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Menu,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  BugReport as DebugIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Cached as CachedIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';

import { useApi } from '../hooks/useApi';
import { LogEntry, LogsResponse, LogStats, LogFilters, LOG_LEVELS, LOG_LEVEL_COLORS, LogLevel } from '../types/LogEntry';

interface LogRowProps {
  log: LogEntry;
}

const LogRow: React.FC<LogRowProps> = React.memo(({ log }) => {
  const [open, setOpen] = useState(false);
  
  const getLevelIcon = (level: string) => {
    switch (level.toLowerCase()) {
      case 'error':
      case 'critical':
        return <ErrorIcon sx={{ color: LOG_LEVEL_COLORS[level as LogLevel] }} />;
      case 'warning':
        return <WarningIcon sx={{ color: LOG_LEVEL_COLORS[level as LogLevel] }} />;
      case 'information':
        return <InfoIcon sx={{ color: LOG_LEVEL_COLORS[level as LogLevel] }} />;
      case 'debug':
      case 'trace':
        return <DebugIcon sx={{ color: LOG_LEVEL_COLORS[level as LogLevel] }} />;
      default:
        return <InfoIcon sx={{ color: LOG_LEVEL_COLORS[level as LogLevel] }} />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
  };

  const truncateCategory = (category: string) => {
    const parts = category.split('.');
    return parts.length > 2 ? `...${parts.slice(-2).join('.')}` : category;
  };

  return (
    <>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' }, height: '48px' }}>
        <TableCell sx={{ width: '60px', padding: '4px 8px' }}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row" sx={{ width: '170px', padding: '4px 8px' }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
            {formatTimestamp(log.timestamp)}
          </Typography>
        </TableCell>
        <TableCell sx={{ width: '120px', padding: '4px 8px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getLevelIcon(log.level)}
            <Chip
              label={log.level}
              size="small"
              sx={{
                backgroundColor: LOG_LEVEL_COLORS[log.level as LogLevel],
                color: 'white',
                fontWeight: 'bold',
                minWidth: 80,
                height: '20px'
              }}
            />
          </Box>
        </TableCell>
        <TableCell sx={{ width: '15%', maxWidth: 180, padding: '4px 8px' }}>
          <Tooltip title={log.category}>
            <Typography variant="body2" sx={{ 
              fontFamily: 'monospace', 
              fontSize: '0.8rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {truncateCategory(log.category)}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell sx={{ width: '15%', maxWidth: 200, padding: '4px 8px' }}>
          {log.documentFileName ? (
            <Tooltip title={log.documentFileName} placement="top">
              <Button
                variant="text"
                size="small"
                startIcon={<LaunchIcon />}
                onClick={() => {
                  // Find the report with this filename to get the full URL
                  // For now, construct a search URL that will show reports with this filename
                  if (log.documentFileName) {
                    const searchParams = new URLSearchParams();
                    searchParams.set('search', log.documentFileName);
                    window.open(`/reports?${searchParams.toString()}`, '_blank');
                  }
                }}
                sx={{
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  padding: '2px 6px',
                  minWidth: 'auto',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                  }
                }}
              >
                <Typography variant="body2" sx={{
                  fontSize: '0.75rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '120px'
                }}>
                  {log.documentFileName}
                </Typography>
              </Button>
            </Tooltip>
          ) : (
            <Typography variant="body2" sx={{ 
              fontSize: '0.75rem',
              color: 'text.disabled',
              fontStyle: 'italic'
            }}>
              -
            </Typography>
          )}
        </TableCell>
        <TableCell sx={{ width: 'auto', minWidth: 350, padding: '4px 8px' }}>
          <Typography 
            variant="body2" 
            sx={{ 
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: 1.2
            }}
          >
            {log.message}
</Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom component="div">
                    Log Details
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, backgroundColor: 'background.paper' }}>
                    <Typography variant="subtitle2" gutterBottom>Message</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {log.message}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, backgroundColor: 'background.paper' }}>
                    <Typography variant="subtitle2" gutterBottom>Details</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      <strong>Category:</strong> {log.category}<br />
                      <strong>Application:</strong> {log.applicationName}<br />
                      {log.eventId && <><strong>Event ID:</strong> {log.eventId}<br /></>}
                    </Typography>
                  </Paper>
                </Grid>
                {log.exception && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, backgroundColor: '#ffebee' }}>
                      <Typography variant="subtitle2" gutterBottom color="error">
                        Exception
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        fontFamily: 'monospace', 
                        fontSize: '0.8rem', 
                        whiteSpace: 'pre-wrap',
                        color: 'error.main'
                      }}>
                        {log.exception}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                {log.properties && Object.keys(log.properties).length > 0 && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, backgroundColor: 'background.paper' }}>
                      <Typography variant="subtitle2" gutterBottom>Properties</Typography>
                      <pre style={{ 
                        fontSize: '0.8rem', 
                        fontFamily: 'monospace',
                        backgroundColor: '#f5f5f5',
                        color: '#333333',
                        padding: '8px',
                        borderRadius: '4px',
                        overflow: 'auto',
                        maxHeight: '200px',
                        border: '1px solid #e0e0e0'
                      }}>
                        {JSON.stringify(log.properties, null, 2)}
                      </pre>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
});

const LogViewer: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [manualLoading, setManualLoading] = useState(false); // Only for manual refresh
  const [initialLoading, setInitialLoading] = useState(true); // Only for initial load
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<LogStats | null>(null);
  const [levels, setLevels] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [expandedFilters, setExpandedFilters] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [refreshMenuAnchor, setRefreshMenuAnchor] = useState<null | HTMLElement>(null);
  
  const [filters, setFilters] = useState<LogFilters>({
    limit: 200
  });

  // Debounced search state for better performance
  const [searchTerms, setSearchTerms] = useState({
    documentFileName: '',
    messageSearch: ''
  });
  const [searchPending, setSearchPending] = useState(false);

  // Debounce search input to avoid excessive API calls
  useEffect(() => {
    // Check if search terms differ from applied filters
    const hasChanges = 
      searchTerms.documentFileName !== (filters.documentFileName || '') ||
      searchTerms.messageSearch !== (filters.messageSearch || '');
    
    setSearchPending(hasChanges);

    const timeoutId = setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        documentFileName: searchTerms.documentFileName || undefined,
        messageSearch: searchTerms.messageSearch || undefined
      }));
      setSearchPending(false);
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [searchTerms, filters.documentFileName, filters.messageSearch]);

  // Apply search filters and fetch logs
  // No longer needed with live filtering
  // const handleSearch = useCallback(() => {
  //   // No longer needed - inputs directly update filters
  // }, []);

  // Clear search filters - no longer needed, use handleClearFilters instead
  // const handleClearSearch = useCallback(() => {
  //   setFilters(prev => ({
  //     ...prev,
  //     messageSearch: undefined,
  //     documentFileName: undefined
  //   }));
  // }, []);

  // Remove Enter key handler since filtering is now live
  // const handleSearchKeyPress = (event: React.KeyboardEvent) => {
  //   if (event.key === 'Enter') {
  //     handleSearch();
  //   }
  // };

  const { callApi } = useApi();

  const fetchLogs = useCallback(async (showLoading = false) => {
    if (showLoading) {
      setManualLoading(true);
    }
    setError(null);
    
    try {
      const params = new URLSearchParams();
      params.append('limit', filters.limit.toString());
      
      if (filters.level) params.append('level', filters.level);
      if (filters.category) params.append('category', filters.category);
      if (filters.startDate) params.append('startDate', filters.startDate.toISOString());
      if (filters.endDate) params.append('endDate', filters.endDate.toISOString());
      if (filters.documentFileName) params.append('documentFileName', filters.documentFileName);
      if (filters.messageSearch) params.append('messageSearch', filters.messageSearch);

      const response = await callApi(`/api/Logs?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch logs');
      }
      
      const data: LogsResponse = await response.json();
      setLogs(data.logs);
      setLastRefresh(new Date());
      
      // Clear initial loading on first successful load
      if (initialLoading) {
        setInitialLoading(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch logs');
    } finally {
      if (showLoading) {
        setManualLoading(false);
      }
    }
  }, [callApi, filters, initialLoading]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await callApi('/api/Logs/stats');
      if (response.ok) {
        const data: LogStats = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch log stats:', err);
    }
  }, [callApi]);

  const fetchMetadata = useCallback(async () => {
    try {
      const [levelsResponse, categoriesResponse] = await Promise.all([
        callApi('/api/Logs/levels'),
        callApi('/api/Logs/categories')
      ]);

      if (levelsResponse.ok) {
        const levelsData = await levelsResponse.json();
        setLevels(levelsData);
      }

      if (categoriesResponse.ok) {
        const categoriesData = await categoriesResponse.json();
        setCategories(categoriesData);
      }
    } catch (err) {
      console.error('Failed to fetch metadata:', err);
    }
  }, [callApi]);

  useEffect(() => {
    fetchLogs(false); // Initial load - don't show manual loading
    fetchStats();
    fetchMetadata();
  }, [fetchLogs, fetchStats, fetchMetadata]);

  // Auto-refresh effect - only refresh logs (most important data)
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchLogs(false); // Auto-refresh without loading indicator
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, fetchLogs]);

  // Auto-refresh stats less frequently (every 30 seconds)
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchStats(); // Refresh stats less frequently
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, fetchStats]);

  const handleRefresh = () => {
    fetchLogs(true); // Show loading for manual refresh
    fetchStats();
    // Don't refresh metadata on manual refresh - it rarely changes
  };

  const handleFullRefresh = () => {
    fetchLogs(true); // Show loading for manual refresh
    fetchStats();
    fetchMetadata(); // Full refresh including metadata
  };

  const handleClearFilters = () => {
    setFilters({ limit: 200 });
    setSearchTerms({ documentFileName: '', messageSearch: '' });
  };

  // Memoize expensive calculations to prevent unnecessary re-renders
  const activeFiltersCount = useMemo(() => [
    filters.level,
    filters.category,
    filters.startDate,
    filters.endDate,
    filters.messageSearch,
    filters.documentFileName
  ].filter(Boolean).length, [filters]);

  // Memoize the logs display (server-side filtering now handles all search)
  const memoizedLogs = useMemo(() => {
    return logs;
  }, [logs]);

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading logs: {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 2 }}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Toolbar sx={{ p: 0 }}>
            <Typography variant="h5" component="div" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              📊 System Logs
              {stats && (
                <Chip 
                  label={`${stats.logCounts.total} total logs`} 
                  size="small" 
                  color="primary" 
                />
              )}
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: autoRefresh ? 'success.main' : 'text.secondary',
                    fontFamily: 'monospace',
                    fontSize: '0.75rem'
                  }}
                >
                  ⏱️ Last updated: {lastRefresh.toLocaleTimeString()}
                </Typography>
                {manualLoading && (
                  <CircularProgress 
                    size={12} 
                    sx={{ 
                      color: autoRefresh ? 'success.main' : 'primary.main' 
                    }} 
                  />
                )}
              </Box>
              <Tooltip 
                title={
                  autoRefresh 
                    ? "Smart auto-refresh is ON - logs update every 10s, stats every 30s" 
                    : "Auto-refresh is OFF - click to enable smart automatic updates"
                }
                arrow
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoRefresh}
                      onChange={(e) => setAutoRefresh(e.target.checked)}
                      color="success"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {autoRefresh ? (
                        <PlayArrowIcon 
                          fontSize="small" 
                          sx={{ 
                            color: 'success.main',
                            animation: 'pulse 2s infinite',
                            '@keyframes pulse': {
                              '0%': { opacity: 1 },
                              '50%': { opacity: 0.5 },
                              '100%': { opacity: 1 },
                            }
                          }} 
                        />
                      ) : (
                        <PauseIcon 
                          fontSize="small" 
                          sx={{ color: 'text.secondary' }} 
                        />
                      )}
                                           <Typography 
                       variant="body2" 
                       sx={{ 
                         color: autoRefresh ? 'success.main' : 'text.secondary',
                         fontWeight: autoRefresh ? 500 : 400
                       }}
                     >
                       Smart Auto-refresh
                     </Typography>
                 </Box>
               }
               sx={{ mr: 1 }}
             />
           </Tooltip>
           <Tooltip title="Auto-refresh: Logs every 10s, Stats every 30s. Click arrow for more options." arrow>
             <span>
              <Button
                variant="outlined"
                startIcon={
                  <Badge badgeContent={activeFiltersCount} color="secondary">
                    <FilterIcon />
                  </Badge>
                }
                onClick={() => setExpandedFilters(!expandedFilters)}
              >
                Filters
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={manualLoading}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setRefreshMenuAnchor(e.currentTarget);
                }}
                endIcon={<ExpandMoreIcon 
                  fontSize="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    setRefreshMenuAnchor(e.currentTarget.closest('button'));
                  }}
                  sx={{ cursor: 'pointer' }}
                />}
              >
                Refresh
              </Button>
              <Menu
                anchorEl={refreshMenuAnchor}
                open={Boolean(refreshMenuAnchor)}
                onClose={() => setRefreshMenuAnchor(null)}
              >
                <MenuItem 
                  onClick={() => {
                    handleRefresh();
                    setRefreshMenuAnchor(null);
                  }}
                >
                  <RefreshIcon sx={{ mr: 1 }} fontSize="small" />
                  Quick Refresh (Logs + Stats)
                </MenuItem>
                <MenuItem 
                  onClick={() => {
                    handleFullRefresh();
                    setRefreshMenuAnchor(null);
                  }}
                >
                  <CachedIcon sx={{ mr: 1 }} fontSize="small" />
                  Full Refresh (Everything)
                </MenuItem>
                <Divider />
                                 <MenuItem 
                   onClick={() => {
                     fetchLogs(true); // Show loading for manual logs refresh
                     setRefreshMenuAnchor(null);
                   }}
                 >
                   <Typography variant="body2" color="text.secondary">
                     📋 Logs Only
                   </Typography>
                 </MenuItem>
                <MenuItem 
                  onClick={() => {
                    fetchStats();
                    setRefreshMenuAnchor(null);
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    📊 Stats Only
                  </Typography>
                                 </MenuItem>
               </Menu>
               </span>
             </Tooltip>
            </Stack>
          </Toolbar>

          {/* Stats Cards */}
          {stats && (
            <Grid 
              container 
              spacing={2} 
              sx={{ 
                mt: 1, 
                mb: 2,
                transition: 'opacity 0.3s ease-in-out',
                opacity: 1
              }}
            >
              <Grid item>
                <Card sx={{ minWidth: 120 }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="h6">{stats.logCounts.last24Hours}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Last 24h
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item>
                <Card sx={{ minWidth: 120 }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="h6">{stats.logCounts.last7Days}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Last 7 days
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item>
                <Card sx={{ minWidth: 120 }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="h6">{stats.logCounts.last30Days}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Last 30 days
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item>
                <Card sx={{ minWidth: 120 }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="h6">{stats.collectionStats.totalDocuments}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total logs
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Filters */}
          <Accordion expanded={expandedFilters} onChange={() => setExpandedFilters(!expandedFilters)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Filter Options</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Documents"
                    placeholder="Enter filename to filter documents..."
                    value={searchTerms.documentFileName || ''}
                    onChange={(e) => setSearchTerms(prev => ({ ...prev, documentFileName: e.target.value }))}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Messages"
                    placeholder="Enter text to filter messages..."
                    value={searchTerms.messageSearch || ''}
                    onChange={(e) => setSearchTerms(prev => ({ ...prev, messageSearch: e.target.value }))}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Level</InputLabel>
                    <Select
                      value={filters.level || ''}
                      label="Level"
                      onChange={(e) => setFilters(prev => ({ ...prev, level: e.target.value || undefined }))}
                    >
                      <MenuItem value="">All Levels</MenuItem>
                      {levels.map(level => (
                        <MenuItem key={level} value={level}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={level}
                              size="small"
                              sx={{
                                backgroundColor: LOG_LEVEL_COLORS[level as LogLevel],
                                color: 'white',
                                fontWeight: 'bold'
                              }}
                            />
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={filters.category || ''}
                      label="Category"
                      onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value || undefined }))}
                    >
                      <MenuItem value="">All Categories</MenuItem>
                      {categories.map(category => (
                        <MenuItem key={category} value={category}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {category}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Start Date"
                    type="datetime-local"
                    value={filters.startDate ? filters.startDate.toISOString().slice(0, 16) : ''}
                    onChange={(e) => setFilters(prev => ({ 
                      ...prev, 
                      startDate: e.target.value ? new Date(e.target.value) : undefined 
                    }))}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={1}>
                  <TextField
                    fullWidth
                    size="small"
                    label="End Date"
                    type="datetime-local"
                    value={filters.endDate ? filters.endDate.toISOString().slice(0, 16) : ''}
                    onChange={(e) => setFilters(prev => ({ 
                      ...prev, 
                      endDate: e.target.value ? new Date(e.target.value) : undefined 
                    }))}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={1}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Limit"
                    type="number"
                    value={filters.limit}
                    onChange={(e) => setFilters(prev => ({ ...prev, limit: Math.min(5000, Math.max(1, parseInt(e.target.value) || 200)) }))}
                    inputProps={{ min: 1, max: 5000 }}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                >
                  Clear All Filters
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Paper>

        {/* Logs Table */}
        <TableContainer 
          component={Paper}
          sx={{
            transition: 'opacity 0.2s ease-in-out',
            opacity: initialLoading ? 0.7 : 1,
            width: '100%',
            overflowX: 'auto'
          }}
        >
          <Table sx={{ minWidth: 550, width: '100%', tableLayout: 'fixed' }} size="small">
            <TableHead>
              <TableRow sx={{ height: '48px' }}>
                <TableCell sx={{ width: '60px', padding: '4px 8px' }} />
                <TableCell sx={{ width: '170px', padding: '4px 8px' }}>Timestamp</TableCell>
                <TableCell sx={{ width: '120px', padding: '4px 8px' }}>Level</TableCell>
                <TableCell sx={{ width: '15%', maxWidth: 180, padding: '4px 8px' }}>Category</TableCell>
                <TableCell sx={{ width: '15%', maxWidth: 200, padding: '4px 8px' }}>Document</TableCell>
                <TableCell sx={{ width: 'auto', minWidth: 350, padding: '4px 8px' }}>Message</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {initialLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Loading logs...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : memoizedLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      No logs found matching the current filters
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                memoizedLogs.map((log) => <LogRow key={log.id} log={log} />)
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {memoizedLogs.length > 0 && (
          <Box sx={{ 
            mt: 2, 
            textAlign: 'center',
            transition: 'opacity 0.2s ease-in-out',
            opacity: initialLoading ? 0.7 : 1
          }}>
            <Typography variant="body2" color="text.secondary">
              Showing {memoizedLogs.length} logs (limited to {filters.limit})
            </Typography>
          </Box>
        )}
      </Box>
  );
};

export default LogViewer; 